import React, { useState, useRef, useEffect } from "react";
import { Typography, Button } from "../../../common";
import { VideoContent } from "../../../../types/newtypes";
import Shimmer from "../ui-helpers/Shimmer";
import { FileText, ChevronDown, ChevronUp, Play } from "lucide-react";
import Player from "@vimeo/player";

// Utility function to extract YouTube video ID and create embed URL
const getYouTubeEmbedUrl = (url: string): string | null => {
  const youtubeRegex =
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
  const match = url.match(youtubeRegex);
  if (match) {
    return `https://www.youtube.com/embed/${match[1]}?autoplay=0&rel=0&modestbranding=1`;
  }
  return null;
};

// Check if URL is a YouTube URL
const isYouTubeUrl = (url: string): boolean => {
  return url.includes("youtube.com") || url.includes("youtu.be");
};

// Check if URL is a Vimeo URL
const isVimeoUrl = (url: string): boolean => {
  return url.includes("vimeo.com") || url.includes("player.vimeo.com");
};

// Check if URL is a direct video URL (GCS-hosted or direct MP4/WebM)
const isDirectVideoUrl = (url: string): boolean => {
  // Check for backend /videos/public/ endpoint or direct video file extensions
  // Also handle signed GCS URLs which have .mp4?X-Goog-... query params
  return (
    url.includes("/videos/public/") ||
    url.includes("storage.googleapis.com") ||
    url.endsWith(".mp4") ||
    url.endsWith(".webm") ||
    url.endsWith(".ogg") ||
    url.includes(".mp4#") ||
    url.includes(".webm#") ||
    url.includes(".ogg#") ||
    url.includes(".mp4?") ||
    url.includes(".webm?") ||
    url.includes(".ogg?")
  );
};

// Process Vimeo URL to handle timestamps and extract times
const getVimeoEmbedInfo = (url: string): { embedUrl: string; startTime?: number; endTime?: number } => {
  const timestampMatch = url.match(/#t=(\d+)(?:,(\d+))?$/);
  const baseUrl = url.split("#")[0];
  const startTime = timestampMatch ? parseInt(timestampMatch[1], 10) : undefined;
  const endTime = timestampMatch?.[2] !== undefined ? parseInt(timestampMatch[2], 10) : undefined;

  let embedUrl = baseUrl;
  if (!baseUrl.includes("player.vimeo.com")) {
    const vimeoMatch = baseUrl.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {embedUrl = `https://player.vimeo.com/video/${vimeoMatch[1]}`;}
  }
  if (startTime !== undefined) {embedUrl = `${embedUrl}#t=${startTime}s`;}
  return { embedUrl, startTime, endTime };
};

// Process direct video URL to extract video source and timestamps
const getDirectVideoInfo = (url: string): { videoUrl: string; startTime?: number; endTime?: number } => {
  const timestampMatch = url.match(/#t=(\d+)(?:,(\d+))?$/);

  if (timestampMatch) {
    const baseUrl = url.split("#")[0];
    const startTime = parseInt(timestampMatch[1], 10);
    const endTime = timestampMatch[2] !== undefined ? parseInt(timestampMatch[2], 10) : undefined;

    return {
      videoUrl: baseUrl,
      startTime,
      endTime
    };
  }

  return { videoUrl: url };
};

interface VideoPreviewProps {
  content: VideoContent;
  onCompleted?: () => void;
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  onCardStateChange?: (isCompleted: boolean, canProceed: boolean) => void;
}

const VideoPreview: React.FC<VideoPreviewProps> = ({ content }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [showTranscript, setShowTranscript] = useState(false);
  const [clipEnded, setClipEnded] = useState(false);
  const [signedVideoUrl, setSignedVideoUrl] = useState<string | null>(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [signedUrlError, setSignedUrlError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const vimeoPlayerRef = useRef<Player | null>(null);
  const transcriptId = "video-transcript";

  // Support both video_url and video_uri field names
  const videoUrl = content?.video_url || content?.video_uri;

  // Check video type
  const isYouTube = videoUrl ? isYouTubeUrl(videoUrl) : false;
  const isVimeo = videoUrl ? isVimeoUrl(videoUrl) : false;
  const isDirectVideo = videoUrl ? isDirectVideoUrl(videoUrl) : false;

  // Get embed/video info based on type
  const youtubeEmbedUrl = isYouTube && videoUrl ? getYouTubeEmbedUrl(videoUrl) : null;
  const vimeoEmbedInfo = isVimeo && videoUrl ? getVimeoEmbedInfo(videoUrl) : null;
  const directVideoInfo = isDirectVideo && videoUrl ? getDirectVideoInfo(videoUrl) : null;

  // Fetch signed URL for direct videos served via our backend
  // This allows Safari to fetch directly from GCS without redirect issues
  useEffect(() => {
    const fetchSignedUrl = async () => {
      if (!directVideoInfo?.videoUrl) {return;}

      // Check if this is a video served via our backend
      const backendVideoMatch = directVideoInfo.videoUrl.match(/\/videos\/public\/(.+)$/);
      if (!backendVideoMatch) {
        // Not a backend video, use the URL directly
        setSignedVideoUrl(directVideoInfo.videoUrl);
        return;
      }

      const videoPath = backendVideoMatch[1];
      // Extract base URL from the original video URL
      const baseUrl = directVideoInfo.videoUrl.replace(/\/videos\/public\/.*$/, "");
      const signedUrlEndpoint = `${baseUrl}/videos/signed-url/${videoPath}`;

      try {
        const response = await fetch(signedUrlEndpoint);
        if (!response.ok) {
          throw new Error(`Failed to get signed URL: ${response.status}`);
        }
        const data = await response.json();
        if (data.signedUrl) {
          setSignedVideoUrl(data.signedUrl);
          setSignedUrlError(null);
        } else {
          throw new Error("No signed URL in response");
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch signed URL, falling back to direct URL:", error);
        // Fallback to original URL (works for Chrome)
        setSignedVideoUrl(directVideoInfo.videoUrl);
        setSignedUrlError(null);
      }
    };

    fetchSignedUrl();
  }, [directVideoInfo?.videoUrl]);

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleVideoLoad = () => {
    setIsLoading(false);
  };

  // Seek to start time when video is ready to play
  const handleVideoCanPlay = () => {
    setIsLoading(false);
    // If there's a start time and we haven't already seeked, seek to it
    if (videoRef.current && directVideoInfo?.startTime !== undefined && videoRef.current.currentTime < directVideoInfo.startTime) {
      videoRef.current.currentTime = directVideoInfo.startTime;
    }
  };

  // Handle video errors
  const handleVideoError = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    const video = e.currentTarget;
    const error = video.error;
    // eslint-disable-next-line no-console
    console.error("Video error:", {
      code: error?.code,
      message: error?.message,
      MEDIA_ERR_ABORTED: error?.code === 1,
      MEDIA_ERR_NETWORK: error?.code === 2,
      MEDIA_ERR_DECODE: error?.code === 3,
      MEDIA_ERR_SRC_NOT_SUPPORTED: error?.code === 4,
    });
    setSignedUrlError(`Video error: ${error?.message || "Unknown error"}`);
    setIsLoading(false);
  };

  // Handle replay for direct video
  const handleReplayDirectVideo = () => {
    setClipEnded(false);
    if (videoRef.current) {
      videoRef.current.currentTime = directVideoInfo?.startTime ?? 0;
      videoRef.current.play();
    }
  };

  const handlePlayButtonClick = () => {
    if (videoRef.current) {
      // Reset to the intended start position before playing.
      // Safari's preload may advance currentTime to the first keyframe (~3 s),
      // so an explicit seek ensures consistent cross-browser behaviour.
      videoRef.current.currentTime = directVideoInfo?.startTime ?? 0;
      videoRef.current.play().catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("Play request was interrupted or blocked:", err);
      });
    }
  };

  // Set up Vimeo player with end time control
  useEffect(() => {
    if (isVimeo && vimeoEmbedInfo && iframeRef.current && vimeoEmbedInfo.endTime) {
      // Initialize Vimeo player
      const player = new Player(iframeRef.current);
      vimeoPlayerRef.current = player;

      // Listen for time updates
      const handleTimeUpdate = (data: { seconds: number }) => {
        // Stop the video if it reaches the end time
        if (vimeoEmbedInfo.endTime && data.seconds >= vimeoEmbedInfo.endTime) {
          player.pause();
          setClipEnded(true);
          // Optionally, seek back to the start of the clip
          if (vimeoEmbedInfo.startTime) {
            player.setCurrentTime(vimeoEmbedInfo.startTime);
          }
        }
      };

      // Reset clip ended state when video starts playing
      const handlePlay = () => {
        setClipEnded(false);
      };

      player.on("play", handlePlay);

      player.on("timeupdate", handleTimeUpdate);

      // Cleanup
      return () => {
        player.off("timeupdate", handleTimeUpdate);
        player.off("play", handlePlay);
        vimeoPlayerRef.current = null;
      };
    }
  }, [isVimeo, vimeoEmbedInfo]);

  // Set up direct video player with timestamp control
  useEffect(() => {
    if (isDirectVideo && directVideoInfo && videoRef.current) {
      const video = videoRef.current;

      // Handle time updates to enforce end time
      const handleTimeUpdate = () => {
        if (directVideoInfo.endTime && video.currentTime >= directVideoInfo.endTime) {
          video.pause();
          setClipEnded(true);
          // Seek back to start of clip
          if (directVideoInfo.startTime) {
            video.currentTime = directVideoInfo.startTime;
          }
        }
      };

      // Reset clip ended state when video starts playing
      const handlePlay = () => {
        setClipEnded(false);
      };

      video.addEventListener("timeupdate", handleTimeUpdate);
      video.addEventListener("play", handlePlay);

      // Cleanup
      return () => {
        video.removeEventListener("timeupdate", handleTimeUpdate);
        video.removeEventListener("play", handlePlay);
      };
    }
  }, [isDirectVideo, directVideoInfo]);

  // Determine if we have a valid video source
  const hasValidVideoSource = youtubeEmbedUrl || vimeoEmbedInfo || directVideoInfo;

  return (
    <div className="w-full">
      {/* Video Title */}
      <Typography variant="h4" weight="bold" className="mb-4">
        {content?.slide_title}
      </Typography>

      {/* Intro Text */}
      <Typography variant="p" className="mb-6">
        {content?.intro_text}
      </Typography>

      {/* Video Player Container */}
      <div className="bg-black rounded-lg overflow-hidden mb-6 max-w-4xl mx-auto">
        {/* Video Player */}
        <div className="relative">
          {isLoading && (
            <div className="absolute inset-0 z-10">
              <Shimmer className="w-full h-full" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-white bg-black bg-opacity-50 px-4 py-2 rounded-lg">
                  Loading video...
                </div>
              </div>
            </div>
          )}

          {/* YouTube or Vimeo iframe player */}
          {(youtubeEmbedUrl || vimeoEmbedInfo) && (
            <div
              className="relative w-full"
              style={{ paddingBottom: "56.25%" }}
            >
              <iframe
                ref={iframeRef}
                src={youtubeEmbedUrl || vimeoEmbedInfo?.embedUrl || ""}
                className="absolute top-0 left-0 w-full h-full"
                frameBorder="0"
                title="Video content"
                aria-describedby={transcriptId}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                onLoad={handleIframeLoad}
              />
              {/* Show overlay when clip ends (Vimeo) */}
              {clipEnded && vimeoEmbedInfo?.endTime && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20">
                  <div className="bg-white rounded-lg px-6 py-4 text-center max-w-sm mx-4">
                    <Typography variant="h6" weight="bold" className="mb-2">
                      End of clip
                    </Typography>
                    <Typography variant="small" className="text-gray-600 mb-3">
                      This clip has ended at {Math.floor(vimeoEmbedInfo.endTime / 60)}:{String(vimeoEmbedInfo.endTime % 60).padStart(2, "0")}
                    </Typography>
                    <Button
                      variant="filled"
                      size="small"
                      onClick={() => {
                        setClipEnded(false);
                        if (vimeoPlayerRef.current) {
                          vimeoPlayerRef.current.play();
                        }
                      }}
                    >
                      Replay Clip
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Direct video player (GCS-hosted, MP4, etc.) */}
          {directVideoInfo && !youtubeEmbedUrl && !vimeoEmbedInfo && (
            <div
              className="relative w-full"
              style={{ paddingBottom: "56.25%" }}
            >
              {/* Show loading while fetching signed URL */}
              {!signedVideoUrl && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                  <div className="text-white bg-black bg-opacity-50 px-4 py-2 rounded-lg">
                    Loading video...
                  </div>
                </div>
              )}
              {signedVideoUrl && (
                <video
                  ref={videoRef}
                  src={signedVideoUrl}
                  className="absolute top-0 left-0 w-full h-full"
                  controls
                  preload="auto"
                  onLoadedData={handleVideoLoad}
                  onCanPlay={handleVideoCanPlay}
                  onError={handleVideoError}
                  onPlay={() => setHasStarted(true)}
                  aria-describedby={transcriptId}
                >
                  Your browser does not support the video tag.
                </video>
              )}
              {/* Centered play button overlay */}
              {signedVideoUrl && !hasStarted && !isLoading && !signedUrlError && !clipEnded && (
                <div
                  className="absolute inset-0 z-[15] flex items-center justify-center bg-black/30 cursor-pointer"
                  data-testid="video-play-overlay"
                  onClick={handlePlayButtonClick}
                >
                  <button
                    type="button"
                    className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform focus:outline-none focus:ring-4 focus:ring-white/50"
                    aria-label="Play video"
                    data-testid="video-play-button"
                  >
                    <Play className="text-slate-900 ml-1" size={36} fill="currentColor" />
                  </button>
                </div>
              )}
              {signedUrlError && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-20">
                  <div className="text-white text-center p-4">
                    <p className="mb-2">Unable to load video</p>
                    <p className="text-sm text-gray-400">{signedUrlError}</p>
                  </div>
                </div>
              )}
              {/* Show overlay when clip ends (Direct video) */}
              {clipEnded && directVideoInfo.endTime && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20">
                  <div className="bg-white rounded-lg px-6 py-4 text-center max-w-sm mx-4">
                    <Typography variant="h6" weight="bold" className="mb-2">
                      End of clip
                    </Typography>
                    <Typography variant="small" className="text-gray-600 mb-3">
                      This clip has ended at {Math.floor(directVideoInfo.endTime / 60)}:{String(directVideoInfo.endTime % 60).padStart(2, "0")}
                    </Typography>
                    <Button
                      variant="filled"
                      size="small"
                      onClick={handleReplayDirectVideo}
                    >
                      Replay Clip
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Invalid video URL fallback */}
          {!hasValidVideoSource && (
            <div
              className="relative w-full"
              style={{ paddingBottom: "56.25%" }}
            >
              <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-gray-900">
                <div className="text-white text-center">
                  <div className="mb-2">Video unavailable</div>
                  <div className="text-sm text-gray-400">
                    Please check the video URL configuration
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Transcript Toggle Button */}
      <div className="flex items-center justify-between mb-4">
        <Button
          variant="text"
          className="flex items-center gap-2 !text-primary transition-colors"
          onClick={() => setShowTranscript(!showTranscript)}
          aria-expanded={showTranscript}
          aria-controls={transcriptId}
        >
          <FileText className="w-4 h-4" />
          <span>{showTranscript ? "Hide Summary" : "Show Summary"}</span>
          {showTranscript ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </Button>
      </div>

      {/* Transcript Section */}
      <div
        id={transcriptId}
        className={`bg-gray-50 rounded-lg p-6 mb-6 transition-all duration-300 ease-in-out ${
          showTranscript ? "block max-h-96 opacity-100 overflow-auto" : "hidden"
        }`}
        role="region"
        aria-label="Video summary"
        aria-hidden={!showTranscript}
      >
        <Typography variant="h6" weight="bold" className="mb-4 max-md:!text-lg">
          Summary
        </Typography>
        <Typography variant="p" className="leading-relaxed max-md:!text-base">
          {content?.transcript}
        </Typography>
      </div>

      {/* Watch instruction */}
      <div className="mt-4 flex items-center gap-2 text-gray-600">
        <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
        <Typography variant="small">Watch the video to continue</Typography>
      </div>
    </div>
  );
};

export default VideoPreview;
